// ═══════════════════════════════════════════════════════════════
// CULTIVATION COMMS TAB — Communication List Builder
// Path: src/components/cultivation/CultivationCommsTab.jsx
// v1.3.0: Contact Preference Codes — automatic opt-out enforcement
//
// Features:
//   - Filter builder (any member field, stackable AND logic)
//   - Column picker with presets (Email Merge, USPS Mail, Custom)
//   - Live results preview table with opt-out indicators
//   - Contact preference enforcement on all outbound actions
//   - Copy Emails to clipboard (semicolon-separated for Outlook)
//   - Open in Email (mailto:?bcc= for default mail client)
//   - CSV export for mail merge
//   - Saved list configurations (Supabase + localStorage fallback)
//   - "Mark as Sent" bulk contact log (direction-aware)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Send, Download, Save, FolderOpen, Trash2, Plus, X, Check,
  Mail, MapPin, Settings2, Filter, Eye, CheckCircle, AlertCircle,
  Copy, ExternalLink, ShieldAlert, DollarSign, ChevronDown,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { formatCurrency } from "../../constants/giftTypes";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { getFiscalDates } from "../../utils/fiscalYear";
import { Card } from "../ui/Card";
import { COMMITTEES } from "../../views/DonorDetailView";

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const MEMBER_FIELDS = [
  { key: "firstName", label: "First Name", group: "Identity", type: "text" },
  { key: "lastName", label: "Last Name", group: "Identity", type: "text" },
  { key: "credentialedFullName", label: "Credentialed Name", group: "Identity", type: "text" },
  { key: "email", label: "Email", group: "Identity", type: "text" },
  { key: "phone", label: "Phone", group: "Identity", type: "text" },
  { key: "title", label: "Title", group: "Identity", type: "text" },
  { key: "organization", label: "Organization", group: "Professional", type: "text" },
  { key: "designation", label: "Designation", group: "Professional", type: "text" },
  { key: "yearsExperience", label: "Years Experience", group: "Professional", type: "text" },
  { key: "afpId", label: "Donor ID", group: "Donor", type: "text" },
  { key: "memberSince", label: "Donor Since", group: "Donor", type: "text" },
  { key: "startDate", label: "Start Date", group: "Donor", type: "text" },
  { key: "endDate", label: "End Date", group: "Donor", type: "text" },
  { key: "homeStreet", label: "Street Address", group: "Address", type: "text" },
  { key: "homeCity", label: "City", group: "Address", type: "text" },
  { key: "homeState", label: "State", group: "Address", type: "text" },
  { key: "homeZip", label: "ZIP Code", group: "Address", type: "text" },
  { key: "homeCountry", label: "Country", group: "Address", type: "text" },
  { key: "pathway", label: "Pathway", group: "Intelligence", type: "select",
    options: ["unengaged", "connect", "contribute", "lead", "prospect"] },
  { key: "isDonor", label: "Is Donor", group: "Intelligence", type: "boolean" },
  { key: "isBoardMember", label: "Is Board Donor", group: "Intelligence", type: "boolean" },
  { key: "assignedDirector", label: "Assigned Director", group: "Intelligence", type: "text" },
  { key: "notes", label: "Notes", group: "Intelligence", type: "text" },
  { key: "committee", label: "Committee", group: "Intelligence", type: "array",
    options: COMMITTEES.map((c) => c.value),
    optionLabels: Object.fromEntries(COMMITTEES.map((c) => [c.value, c.label])) },
  // ── Contact Preferences (v1.3.0) ────────────────────────────
  { key: "noEmail", label: "No Email", group: "Preferences", type: "boolean" },
  { key: "noMail", label: "No Mail", group: "Preferences", type: "boolean" },
  { key: "noPhone", label: "No Phone", group: "Preferences", type: "boolean" },
  { key: "noText", label: "No Text", group: "Preferences", type: "boolean" },
];

const FILTER_OPERATORS = {
  text: [
    { id: "equals", label: "equals" }, { id: "contains", label: "contains" },
    { id: "starts_with", label: "starts with" }, { id: "not_empty", label: "is not empty" },
    { id: "is_empty", label: "is empty" }, { id: "not_equals", label: "does not equal" },
  ],
  boolean: [{ id: "is_true", label: "is true" }, { id: "is_false", label: "is false" }],
  select: [{ id: "equals", label: "equals" }, { id: "not_equals", label: "does not equal" }],
  array: [
    { id: "array_includes", label: "includes" }, { id: "array_excludes", label: "does not include" },
    { id: "array_not_empty", label: "has any assignment" }, { id: "array_is_empty", label: "has no assignment" },
  ],
};

const COLUMN_PRESETS = {
  email: { label: "Email Merge", icon: Mail, columns: ["firstName", "lastName", "email"] },
  usps: { label: "USPS Mail", icon: MapPin, columns: ["firstName", "lastName", "homeStreet", "homeCity", "homeState", "homeZip"] },
  custom: { label: "Custom", icon: Settings2, columns: [] },
};

const COMM_CHANNELS = [
  { id: "outbound_email", label: "Outbound Email", icon: "📧" },
  { id: "outbound_usps_mail", label: "Outbound USPS Mail", icon: "✉️" },
];

// ═══════════════════════════════════════════════════════════════
// CONTACT PREFERENCE ENFORCEMENT (v1.3.0)
// ═══════════════════════════════════════════════════════════════

const getExclusionFlag = (action, preset, channel) => {
  if (action === "copy_emails" || action === "open_in_email") return "noEmail";
  if (action === "export" && preset === "email") return "noEmail";
  if (action === "export" && preset === "usps") return "noMail";
  if (action === "mark_sent" && channel === "outbound_email") return "noEmail";
  if (action === "mark_sent" && channel === "outbound_usps_mail") return "noMail";
  return null;
};

const getExclusionLabel = (flag) => {
  const labels = { noEmail: "email", noMail: "mail", noPhone: "phone", noText: "text" };
  return labels[flag] || flag;
};

const applyExclusion = (members, flag) => {
  if (!flag) return { included: members, excluded: [] };
  const included = [], excluded = [];
  for (const m of members) { if (m[flag] === true) excluded.push(m); else included.push(m); }
  return { included, excluded };
};

const getPresetExclusionFlag = (preset) => {
  if (preset === "email") return "noEmail";
  if (preset === "usps") return "noMail";
  return null;
};

const hasAnyOptOut = (member) => member.noEmail || member.noMail || member.noPhone || member.noText;

const getActiveOptOuts = (member) => {
  const outs = [];
  if (member.noEmail) outs.push("Email");
  if (member.noMail) outs.push("Mail");
  if (member.noPhone) outs.push("Phone");
  if (member.noText) outs.push("Text");
  return outs;
};

// ═══════════════════════════════════════════════════════════════
// SUPABASE SERVICE (with localStorage fallback)
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEY = "cmm_saved_comm_lists";

const commListService = {
  async loadLists(supabase) {
    if (supabase) {
      try {
        const { data, error } = await supabase.from("saved_comm_lists").select("*").order("updated_at", { ascending: false });
        if (!error && data) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {} return data; }
      } catch {}
    }
    try { const stored = localStorage.getItem(STORAGE_KEY); return stored ? JSON.parse(stored) : []; } catch { return []; }
  },
  async saveList(supabase, list, userId) {
    const record = { name: list.name, description: list.description || "", filters: list.filters, columns: list.columns, preset: list.preset || "custom", updated_at: new Date().toISOString() };
    if (supabase) {
      try {
        if (list.id) { const { data, error } = await supabase.from("saved_comm_lists").update(record).eq("id", list.id).select().single(); if (!error && data) return data; }
        else { const { data, error } = await supabase.from("saved_comm_lists").insert({ ...record, created_by: userId }).select().single(); if (!error && data) return data; }
      } catch {}
    }
    const localList = { ...record, id: list.id || crypto.randomUUID(), created_by: userId };
    try { const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); const idx = stored.findIndex((s) => s.id === localList.id); if (idx >= 0) stored[idx] = localList; else stored.unshift(localList); localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch {}
    return localList;
  },
  async deleteList(supabase, listId) {
    if (supabase) { try { const { error } = await supabase.from("saved_comm_lists").delete().eq("id", listId); if (!error) { try { const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.filter((s) => s.id !== listId))); } catch {} return true; } } catch {} }
    try { const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.filter((s) => s.id !== listId))); } catch {}
    return true;
  },
};

// ═══════════════════════════════════════════════════════════════
// FILTER ENGINE
// ═══════════════════════════════════════════════════════════════

const applyFilter = (member, filter) => {
  const { field, operator, value } = filter;
  const raw = member[field];
  if (operator === "array_includes") return (Array.isArray(raw) ? raw : []).includes(value);
  if (operator === "array_excludes") return !(Array.isArray(raw) ? raw : []).includes(value);
  if (operator === "array_not_empty") return Array.isArray(raw) && raw.length > 0;
  if (operator === "array_is_empty") return !Array.isArray(raw) || raw.length === 0;
  const memberVal = (raw === true ? "true" : raw === false ? "false" : String(raw || "")).toLowerCase();
  const filterVal = (value || "").toLowerCase();
  switch (operator) {
    case "equals": return memberVal === filterVal;
    case "not_equals": return memberVal !== filterVal;
    case "contains": return memberVal.includes(filterVal);
    case "starts_with": return memberVal.startsWith(filterVal);
    case "not_empty": return memberVal.trim() !== "" && memberVal !== "undefined" && memberVal !== "null";
    case "is_empty": return memberVal.trim() === "" || memberVal === "undefined" || memberVal === "null";
    case "is_true": return raw === true || memberVal === "true" || memberVal === "yes";
    case "is_false": return raw !== true && memberVal !== "true" && memberVal !== "yes";
    default: return true;
  }
};

const applyFilters = (members, filters) => {
  if (!filters || filters.length === 0) return members;
  return members.filter((m) => filters.every((f) => applyFilter(m, f)));
};

// ═══════════════════════════════════════════════════════════════
// CSV EXPORT
// ═══════════════════════════════════════════════════════════════

const exportCSV = async (members, columns) => {
  const fields = MEMBER_FIELDS.filter((f) => columns.includes(f.key));
  const header = fields.map((f) => f.label).join(",");
  const rows = members.map((m) => fields.map((f) => {
    const raw = m[f.key]; const val = Array.isArray(raw) ? raw.join("; ") : String(raw || "");
    return val.includes(",") || val.includes('"') || val.includes("\n") ? `"${val.replace(/"/g, '""')}"` : val;
  }).join(","));
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const suggestedName = `comm_list_${new Date().toISOString().slice(0, 10)}.csv`;
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({ suggestedName, types: [{ description: "CSV File", accept: { "text/csv": [".csv"] } }] });
      const writable = await handle.createWritable(); await writable.write(blob); await writable.close(); return;
    } catch (err) { if (err.name === "AbortError") return; }
  }
  const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = suggestedName; a.click(); URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════════
// EMAIL HELPERS
// ═══════════════════════════════════════════════════════════════

const extractEmails = (members) => {
  const emails = []; let skipped = 0;
  for (const m of members) {
    const email = (m.email || "").trim();
    if (email && email !== "undefined" && email !== "null" && email.includes("@")) emails.push(email); else skipped++;
  }
  return { emails, skipped };
};

const MAX_MAILTO_LENGTH = 2000;

// ═══════════════════════════════════════════════════════════════
// FILTER ROW COMPONENT
// ═══════════════════════════════════════════════════════════════

const FilterRow = ({ filter, index, onChange, onRemove, palette }) => {
  const fieldDef = MEMBER_FIELDS.find((f) => f.key === filter.field);
  const operators = FILTER_OPERATORS[fieldDef?.type || "text"];
  const needsValue = !["not_empty", "is_empty", "is_true", "is_false", "array_not_empty", "array_is_empty"].includes(filter.operator);
  const inputStyle = { flex: 1, padding: "7px 10px", borderRadius: 6, fontSize: 12, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <select value={filter.field} onChange={(e) => onChange(index, { ...filter, field: e.target.value, operator: "equals", value: "" })}
        style={{ flex: "0 0 160px", padding: "7px 10px", borderRadius: 6, fontSize: 12, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }}>
        <option value="">Select field...</option>
        {Object.entries(MEMBER_FIELDS.reduce((acc, f) => { (acc[f.group] = acc[f.group] || []).push(f); return acc; }, {})).map(([group, fields]) => (
          <optgroup key={group} label={group}>{fields.map((f) => (<option key={f.key} value={f.key}>{f.label}</option>))}</optgroup>
        ))}
      </select>
      <select value={filter.operator} onChange={(e) => onChange(index, { ...filter, operator: e.target.value })}
        style={{ flex: "0 0 150px", padding: "7px 10px", borderRadius: 6, fontSize: 12, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }}>
        {operators.map((op) => (<option key={op.id} value={op.id}>{op.label}</option>))}
      </select>
      {needsValue && (fieldDef?.type === "select" ? (
        <select value={filter.value} onChange={(e) => onChange(index, { ...filter, value: e.target.value })} style={inputStyle}>
          <option value="">Select...</option>{fieldDef.options.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
      ) : fieldDef?.type === "array" ? (
        <select value={filter.value} onChange={(e) => onChange(index, { ...filter, value: e.target.value })} style={inputStyle}>
          <option value="">Select committee...</option>{(fieldDef.options || []).map((o) => (<option key={o} value={o}>{fieldDef.optionLabels?.[o] || o}</option>))}
        </select>
      ) : (
        <input value={filter.value} onChange={(e) => onChange(index, { ...filter, value: e.target.value })} placeholder="Value..." style={inputStyle} />
      ))}
      <button onClick={() => onRemove(index)} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", cursor: "pointer", flexShrink: 0 }}><X size={ICON_SIZES.sm} /></button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MARK AS SENT DIALOG (v1.3.0: live exclusion count by channel)
// ═══════════════════════════════════════════════════════════════

const MarkAsSentDialog = ({ filteredMembers, palette, onConfirm, onCancel }) => {
  const [channel, setChannel] = useState("outbound_email");
  const [note, setNote] = useState("");
  const [sendDate, setSendDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const flag = getExclusionFlag("mark_sent", null, channel);
  const { included, excluded } = applyExclusion(filteredMembers, flag);

  const handleConfirm = async () => { setSaving(true); await onConfirm({ channel, note, date: sendDate }); setSaving(false); };

  return (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1100 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: Math.min(440, typeof window !== "undefined" ? window.innerWidth - 32 : 420), background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", zIndex: 1101, padding: "24px 28px", fontFamily: FONT_FAMILY }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1f2937" }}>Mark as Sent</h3>
          <button onClick={onCancel} style={{ background: "#f3f4f6", border: "none", fontSize: 14, cursor: "pointer", padding: "4px 10px", borderRadius: 6, color: "#6b7280", fontWeight: 700 }}>✕</button>
        </div>
        <div style={{ background: `${palette.accent1}08`, border: `1px solid ${palette.accent1}25`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: palette.text1, fontWeight: 600, textAlign: "center" }}>
          This will log an outbound contact entry on <strong>{included.length}</strong> member{included.length !== 1 ? "s" : ""}
        </div>
        {excluded.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 14, borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, fontWeight: 600, color: "#92400e" }}>
            <ShieldAlert size={ICON_SIZES.sm} style={{ flexShrink: 0 }} />
            <span>{excluded.length} member{excluded.length !== 1 ? "s" : ""} excluded — opted out of {getExclusionLabel(flag)}</span>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>Communication Channel</label>
          <div style={{ display: "flex", gap: 8 }}>
            {COMM_CHANNELS.map((ch) => (
              <button key={ch.id} onClick={() => setChannel(ch.id)} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY, textAlign: "center", border: channel === ch.id ? `2px solid ${palette.accent1}` : "1px solid #e5e7eb", background: channel === ch.id ? `${palette.accent1}10` : "#fff", color: channel === ch.id ? palette.accent1 : "#6b7280" }}>
                <span style={{ fontSize: 18, display: "block", marginBottom: 4 }}>{ch.icon}</span>{ch.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>Date Sent</label>
          <input type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, border: "1px solid #e5e7eb", fontFamily: FONT_FAMILY, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>Note (appears on each contact log)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., AFP Spring membership drive, Annual appeal letter" rows={2} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, border: "1px solid #e5e7eb", fontFamily: FONT_FAMILY, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleConfirm} disabled={saving || included.length === 0} style={{ flex: 1, padding: "10px 16px", borderRadius: 8, border: "none", background: included.length > 0 ? palette.accent1 : "#e5e7eb", color: included.length > 0 ? "#fff" : "#9ca3af", fontSize: 14, fontWeight: 700, cursor: saving ? "wait" : included.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Logging..." : `Log ${included.length} Contact${included.length !== 1 ? "s" : ""}`}
          </button>
          <button onClick={onCancel} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY }}>Cancel</button>
        </div>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// OPT-OUT INDICATOR BADGE (v1.3.0)
// ═══════════════════════════════════════════════════════════════

const OptOutBadge = ({ labels, small }) => (
  <span title={`Opted out of: ${labels.join(", ")}`}
    style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: small ? 9 : 10, fontWeight: 700, color: "#b45309", background: "#fef3c7", border: "1px solid #fde68a", padding: small ? "1px 5px" : "2px 7px", borderRadius: 4, whiteSpace: "nowrap", lineHeight: 1.3 }}>
    <ShieldAlert size={small ? 10 : 11} />
    {labels.length === 1 ? `No ${labels[0]}` : `${labels.length} opt-outs`}
  </span>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export const CultivationCommsTab = ({ enriched }) => {
  const { palette, members, addContactLog, showMessage, currentUser, activeFunds, chapterSettings } = useApp();
  const [filters, setFilters] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(["firstName", "lastName", "email"]);
  const [activePreset, setActivePreset] = useState("email");
  const [savedLists, setSavedLists] = useState([]);
  const [listName, setListName] = useState("");
  const [editingListId, setEditingListId] = useState(null);
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [showMarkSent, setShowMarkSent] = useState(false);
  const [listsLoading, setListsLoading] = useState(true);
  const actionBarRef = useRef(null);

  const openSavePanelAndScroll = () => {
    setShowSavePanel(true);
    setShowLoadPanel(false);
    setTimeout(() => actionBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const openLoadPanelAndScroll = () => {
    setShowLoadPanel(true);
    setShowSavePanel(false);
    setTimeout(() => actionBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  // ── Giving filter state ─────────────────────────────────────
  const [givingFilterOpen, setGivingFilterOpen] = useState(false);
  const [givingMinAmount, setGivingMinAmount] = useState("500");
  const [excludeNoEmail, setExcludeNoEmail] = useState(false);
  const [excludeNoMail, setExcludeNoMail] = useState(false);
  const [excludeNoPhone, setExcludeNoPhone] = useState(false);
  const [excludeNoText, setExcludeNoText] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [givingDateFrom, setGivingDateFrom] = useState("");
  const [givingDateTo, setGivingDateTo] = useState("");
  const [givingFundId, setGivingFundId] = useState(""); // "" = all funds
  const [givingMatchIds, setGivingMatchIds] = useState(null); // null = not active, Set = active
  const [givingLoading, setGivingLoading] = useState(false);
  const [givingResults, setGivingResults] = useState(null); // { memberTotals: Map<id, total> }

  const runGivingQuery = useCallback(async () => {
    const minAmt = parseFloat(givingMinAmount) || 0;
    if (minAmt <= 0) { showMessage?.("Enter a minimum amount greater than 0", "error"); return; }
    if (!givingDateFrom || !givingDateTo) { showMessage?.("Select both a start and end date", "error"); return; }
    setGivingLoading(true);
    try {
      let allRows = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, member_id, amount")
          .gte("gift_date", givingDateFrom)
          .lte("gift_date", givingDateTo)
          .not("amount", "is", null)
          .order("id", { ascending: true })
          .limit(pageSize);
        if (givingFundId) query = query.eq("fund_id", givingFundId);
        if (lastId) query = query.gt("id", lastId);
        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRows = allRows.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }
      // Aggregate by member (stringify IDs for consistent matching)
      const totals = new Map();
      for (const row of allRows) {
        if (!row.member_id) continue;
        const mid = String(row.member_id);
        totals.set(mid, (totals.get(mid) || 0) + (row.amount || 0));
      }
      // Filter to those >= minAmt
      const matchIds = new Set();
      for (const [memberId, total] of totals) {
        if (total >= minAmt) matchIds.add(memberId);
      }
      setGivingMatchIds(matchIds);
      setGivingResults({ memberTotals: totals, matchCount: matchIds.size, totalDonors: totals.size, giftCount: allRows.length });
      showMessage?.(`Found ${matchIds.size} donor${matchIds.size !== 1 ? "s" : ""} with ${formatCurrency(minAmt)}+ in giving`);
    } catch (err) {
      showMessage?.("Giving query failed — " + err.message, "error");
    } finally {
      setGivingLoading(false);
    }
  }, [givingMinAmount, givingDateFrom, givingDateTo, givingFundId, showMessage]);

  const clearGivingFilter = () => {
    setGivingMatchIds(null);
    setGivingResults(null);
    setGivingFundId("");
  };

  useEffect(() => { const load = async () => { setListsLoading(true); const lists = await commListService.loadLists(supabase); setSavedLists(lists); setListsLoading(false); }; load(); }, []);

  const filteredByFields = useMemo(() => applyFilters(members, filters), [members, filters]);
  const filteredMembers = useMemo(() => {
    let result = filteredByFields;
    if (givingMatchIds) {
      result = result.filter((m) => givingMatchIds.has(String(m.id)));
    }
    if (excludeNoEmail) result = result.filter((m) => !m.noEmail);
    if (excludeNoMail) result = result.filter((m) => !m.noMail);
    if (excludeNoPhone) result = result.filter((m) => !m.noPhone);
    if (excludeNoText) result = result.filter((m) => !m.noText);
    if (requireEmail) result = result.filter((m) => {
      const email = (m.email || "").trim();
      return email && email !== "undefined" && email !== "null" && email.includes("@");
    });
    return result;
  }, [filteredByFields, givingMatchIds, excludeNoEmail, excludeNoMail, excludeNoPhone, excludeNoText, requireEmail]);

  // v1.3.0: Preset-aware exclusion for preview
  const presetFlag = getPresetExclusionFlag(activePreset);
  const exclusionInfo = useMemo(() => {
    if (!presetFlag) { const withOptOuts = filteredMembers.filter(hasAnyOptOut); return { flag: null, excludedCount: withOptOuts.length, label: null }; }
    const { included, excluded } = applyExclusion(filteredMembers, presetFlag);
    return { flag: presetFlag, excludedCount: excluded.length, includedCount: included.length, label: getExclusionLabel(presetFlag) };
  }, [filteredMembers, presetFlag]);

  // Email extraction from noEmail-excluded list
  const emailData = useMemo(() => { const { included } = applyExclusion(filteredMembers, "noEmail"); return extractEmails(included); }, [filteredMembers]);
  const emailExcludedCount = useMemo(() => filteredMembers.filter((m) => m.noEmail === true).length, [filteredMembers]);

  const addFilter = () => setFilters([...filters, { field: "pathway", operator: "equals", value: "" }]);
  const updateFilter = (index, updated) => setFilters(filters.map((f, i) => (i === index ? updated : f)));
  const removeFilter = (index) => setFilters(filters.filter((_, i) => i !== index));
  const clearFilters = () => { setFilters([]); setEditingListId(null); setListName(""); };
  const applyPreset = (presetKey) => { setActivePreset(presetKey); if (presetKey !== "custom") setSelectedColumns([...COLUMN_PRESETS[presetKey].columns]); };
  const toggleColumn = (key) => { setActivePreset("custom"); setSelectedColumns((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]); };

  const handleSave = async () => {
    if (!listName.trim()) { showMessage?.("Enter a name for this list", "error"); return; }
    const list = { id: editingListId || undefined, name: listName.trim(), filters, columns: selectedColumns, preset: activePreset };
    const saved = await commListService.saveList(supabase, list, currentUser?.id);
    setSavedLists((prev) => { const idx = prev.findIndex((s) => s.id === saved.id); if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; } return [saved, ...prev]; });
    setEditingListId(saved.id); setShowSavePanel(false); showMessage?.(`List "${saved.name}" saved`);
  };

  const handleLoad = (list) => {
    setFilters(list.filters || []); setSelectedColumns(list.columns || ["firstName", "lastName", "email"]);
    setActivePreset(list.preset || "custom"); setListName(list.name); setEditingListId(list.id);
    setShowLoadPanel(false); showMessage?.(`Loaded "${list.name}"`);
  };

  const handleDelete = async (listId) => {
    await commListService.deleteList(supabase, listId);
    setSavedLists((prev) => prev.filter((s) => s.id !== listId));
    if (editingListId === listId) { setEditingListId(null); setListName(""); }
    showMessage?.("List deleted");
  };

  // v1.3.0: Mark as Sent with exclusion enforcement
  const handleMarkAsSent = async ({ channel, note, date }) => {
    const flag = getExclusionFlag("mark_sent", null, channel);
    const { included, excluded } = applyExclusion(filteredMembers, flag);
    let successCount = 0;
    const contactType = channel === "outbound_email" ? "email" : "letter";
    for (const member of included) {
      try {
        await addContactLog?.(member.id, { type: contactType, direction: "outbound", notes: note || `${channel === "outbound_email" ? "Email" : "USPS Mail"} communication`, date, source: "comm_list_builder" });
        successCount++;
      } catch (err) { console.error(`Failed to log contact for ${member.firstName} ${member.lastName}:`, err); }
    }
    setShowMarkSent(false);
    const excMsg = excluded.length > 0 ? ` (${excluded.length} excluded — opted out of ${getExclusionLabel(flag)})` : "";
    const failCount = included.length - successCount;
    const failMsg = failCount > 0 ? ` (${failCount} failed)` : "";
    showMessage?.(`Logged ${successCount} outbound contact${successCount !== 1 ? "s" : ""}${excMsg}${failMsg}`, failCount > 0 ? "error" : undefined);
  };

  // v1.3.0: Export CSV with exclusion enforcement
  const handleExport = async () => {
    if (filteredMembers.length === 0) { showMessage?.("No members match your filters", "error"); return; }
    if (selectedColumns.length === 0) { showMessage?.("Select at least one column to export", "error"); return; }
    const flag = getExclusionFlag("export", activePreset, null);
    const { included, excluded } = applyExclusion(filteredMembers, flag);
    if (included.length === 0) { showMessage?.(`All ${filteredMembers.length} members are opted out of ${getExclusionLabel(flag)} — nothing to export`, "error"); return; }
    await exportCSV(included, selectedColumns);
    const excMsg = excluded.length > 0 ? ` (${excluded.length} excluded — opted out of ${getExclusionLabel(flag)})` : "";
    showMessage?.(`Exported ${included.length} records${excMsg}`);
  };

  // v1.3.0: Copy Emails with exclusion enforcement
  const handleCopyEmails = async () => {
    const { emails, skipped } = emailData;
    if (emails.length === 0) { showMessage?.(emailExcludedCount > 0 ? "No valid emails — some members opted out of email" : "No valid email addresses in this list", "error"); return; }
    const emailString = emails.join("; ");
    try {
      await navigator.clipboard.writeText(emailString);
      const parts = [`Copied ${emails.length} email${emails.length !== 1 ? "s" : ""}`];
      if (emailExcludedCount > 0) parts.push(`${emailExcludedCount} excluded — opted out`);
      if (skipped > 0) parts.push(`${skipped} had no email`);
      showMessage?.(parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0]);
    } catch {
      const textArea = document.createElement("textarea"); textArea.value = emailString; textArea.style.position = "fixed"; textArea.style.opacity = "0";
      document.body.appendChild(textArea); textArea.select(); document.execCommand("copy"); document.body.removeChild(textArea);
      showMessage?.(`Copied ${emails.length} email${emails.length !== 1 ? "s" : ""}`);
    }
  };

  // v1.3.0: Open in Email with exclusion enforcement
  const handleOpenInEmail = () => {
    const { emails, skipped } = emailData;
    if (emails.length === 0) { showMessage?.(emailExcludedCount > 0 ? "No valid emails — some members opted out of email" : "No valid email addresses in this list", "error"); return; }
    const bccString = emails.join(",");
    const mailtoUrl = `mailto:?bcc=${encodeURIComponent(bccString)}`;
    if (mailtoUrl.length > MAX_MAILTO_LENGTH) { showMessage?.(`${emails.length} addresses is too many for mailto: — emails copied to clipboard instead. Paste into BCC.`, "error"); navigator.clipboard.writeText(emails.join("; ")).catch(() => {}); return; }
    window.location.href = mailtoUrl;
    const parts = [`Opening email client with ${emails.length} BCC recipient${emails.length !== 1 ? "s" : ""}`];
    if (emailExcludedCount > 0) parts.push(`${emailExcludedCount} excluded — opted out`);
    if (skipped > 0) parts.push(`${skipped} had no email`);
    showMessage?.(parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0]);
  };

  const previewFields = MEMBER_FIELDS.filter((f) => selectedColumns.includes(f.key));

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
      {/* Top Action Bar */}
      <div ref={actionBarRef} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Send size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: palette.text1 }}>Communication List Builder</span>
          {editingListId && (<span style={{ fontSize: 11, fontWeight: 600, color: palette.accent1, background: `${palette.accent1}10`, padding: "2px 8px", borderRadius: 4 }}>{listName}</span>)}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowLoadPanel(!showLoadPanel)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.text1 }}>
            <FolderOpen size={ICON_SIZES.sm} /> Load{savedLists.length > 0 && <span style={{ fontSize: 10, color: palette.text3 }}>({savedLists.length})</span>}
          </button>
          <button onClick={() => setShowSavePanel(!showSavePanel)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY, border: `1px solid ${palette.accent1}40`, background: `${palette.accent1}10`, color: palette.accent1 }}>
            <Save size={ICON_SIZES.sm} /> Save
          </button>
        </div>
      </div>

      {/* Save Panel */}
      {showSavePanel && (
        <Card style={{ marginBottom: 12, padding: "14px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input value={listName} onChange={(e) => setListName(e.target.value)} placeholder="List name (e.g., All Prospects, Board Donors)" style={{ flex: 1, padding: "8px 12px", borderRadius: 6, fontSize: 13, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }} />
            <button onClick={handleSave} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: palette.accent1, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT_FAMILY }}>{editingListId ? "Update" : "Save"}</button>
            <button onClick={() => setShowSavePanel(false)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, cursor: "pointer", fontFamily: FONT_FAMILY }}>Cancel</button>
          </div>
        </Card>
      )}

      {/* Load Panel */}
      {showLoadPanel && (
        <Card style={{ marginBottom: 12, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: palette.text1, marginBottom: 10 }}>Saved Lists</div>
          {listsLoading ? (<div style={{ fontSize: 12, color: palette.text3, padding: 8 }}>Loading...</div>
          ) : savedLists.length === 0 ? (<div style={{ fontSize: 12, color: palette.text3, padding: 8 }}>No saved lists yet. Build a filter and save it.</div>
          ) : (savedLists.map((list) => (
            <div key={list.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 6, marginBottom: 4, border: `1px solid ${palette.cardBorder}`, transition: "all 0.12s" }}>
              <div onClick={() => handleLoad(list)} style={{ flex: 1, cursor: "pointer" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: palette.text1 }}>{list.name}</div>
                <div style={{ fontSize: 11, color: palette.text3 }}>{(list.filters || []).length} filter{(list.filters || []).length !== 1 ? "s" : ""} · {list.preset || "custom"} preset</div>
              </div>
              <button onClick={() => handleDelete(list.id)} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", cursor: "pointer" }}><Trash2 size={ICON_SIZES.xs} /></button>
            </div>
          )))}
          <button onClick={() => setShowLoadPanel(false)} style={{ marginTop: 8, padding: "6px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 12, cursor: "pointer", fontFamily: FONT_FAMILY }}>Close</button>
        </Card>
      )}

      {/* Two-Column Layout: Filters + Columns */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, marginBottom: 16 }}>
        <Card style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: palette.text1 }}>Filters</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: filteredMembers.length > 0 ? "#dcfce7" : "#fef3c7", color: filteredMembers.length > 0 ? "#166534" : "#92400e" }}>{filteredMembers.length} match{filteredMembers.length !== 1 ? "es" : ""}</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {filters.length > 0 && (<button onClick={clearFilters} style={{ fontSize: 11, color: "#dc2626", background: "#fee2e2", border: "none", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontFamily: FONT_FAMILY }}>Clear All</button>)}
              <button onClick={addFilter} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: palette.accent1, background: `${palette.accent1}10`, border: `1px solid ${palette.accent1}30`, padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontFamily: FONT_FAMILY }}><Plus size={ICON_SIZES.sm} /> Add Filter</button>
            </div>
          </div>
          {filters.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", color: palette.text3, fontSize: 13 }}>No filters — showing all {members.length} members. Click "Add Filter" to narrow the list.</div>
          ) : filters.map((f, i) => (<FilterRow key={i} filter={f} index={i} onChange={updateFilter} onRemove={removeFilter} palette={palette} />))}
        </Card>

        <Card style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: palette.text1, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Eye size={ICON_SIZES.sm} style={{ color: palette.accent1 }} /> Export Columns</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {Object.entries(COLUMN_PRESETS).map(([key, preset]) => {
              const isActive = activePreset === key; const PresetIcon = preset.icon;
              return (<button key={key} onClick={() => applyPreset(key)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "8px 4px", borderRadius: 8, fontSize: 10, fontWeight: isActive ? 700 : 500, cursor: "pointer", fontFamily: FONT_FAMILY, border: isActive ? `2px solid ${palette.accent1}` : "1px solid #e5e7eb", background: isActive ? `${palette.accent1}10` : "#fff", color: isActive ? palette.accent1 : palette.text2 }}><PresetIcon size={ICON_SIZES.sm} />{preset.label}</button>);
            })}
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {Object.entries(MEMBER_FIELDS.reduce((acc, f) => { (acc[f.group] = acc[f.group] || []).push(f); return acc; }, {})).map(([group, fields]) => (
              <div key={group} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: palette.text3, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{group}</div>
                {fields.map((f) => { const checked = selectedColumns.includes(f.key); return (
                  <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 4, cursor: "pointer", fontSize: 12, color: palette.text1, background: checked ? `${palette.accent1}06` : "transparent" }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleColumn(f.key)} style={{ accentColor: palette.accent1 }} />{f.label}
                  </label>
                ); })}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Giving Filter Panel */}
      <Card style={{ padding: 0, marginBottom: 16, overflow: "hidden" }}>
        <div
          onClick={() => setGivingFilterOpen(!givingFilterOpen)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 18px", cursor: "pointer", userSelect: "none",
            background: givingMatchIds ? `${palette.accent1}06` : "transparent",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DollarSign size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: palette.text1 }}>Giving Filter</span>
            {givingMatchIds && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "#dcfce7", color: "#166534" }}>
                {givingMatchIds.size} donor{givingMatchIds.size !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3, transform: givingFilterOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
        </div>
        {givingFilterOpen && (
          <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${palette.cardBorder}` }}>
            <div style={{ fontSize: 12, color: palette.text3, marginTop: 12, marginBottom: 8 }}>
              Find donors whose total giving in a date range meets or exceeds a threshold.
            </div>
            {/* Quick date range buttons */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
              {(() => {
                const today = new Date();
                const fmt = (d) => d.toISOString().slice(0, 10);
                const fy = getFiscalDates(chapterSettings?.fiscalYearStartMonth || 1);
                const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
                const last7 = new Date(today); last7.setDate(last7.getDate() - 7);
                const last30 = new Date(today); last30.setDate(last30.getDate() - 30);
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                const prevMonthStart = new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), 1);
                const yearStart = new Date(today.getFullYear(), 0, 1);
                const ranges = [
                  { label: "Today", from: fmt(today), to: fmt(today) },
                  { label: "Yesterday", from: fmt(yesterday), to: fmt(yesterday) },
                  { label: "Last 7 Days", from: fmt(last7), to: fmt(today) },
                  { label: "Last 30 Days", from: fmt(last30), to: fmt(today) },
                  { label: "This Month", from: fmt(monthStart), to: fmt(today) },
                  { label: "Last Month", from: fmt(prevMonthStart), to: fmt(prevMonthEnd) },
                  { label: "Calendar Year", from: fmt(yearStart), to: fmt(today) },
                  { label: fy.label, from: fy.yearStart, to: fy.yearEnd },
                ];
                return ranges.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => { setGivingDateFrom(r.from); setGivingDateTo(r.to); }}
                    style={{
                      padding: "4px 10px", borderRadius: 5, border: "none",
                      fontSize: "0.65rem", fontWeight: 600, fontFamily: FONT_FAMILY,
                      cursor: "pointer",
                      background: givingDateFrom === r.from && givingDateTo === r.to ? palette.accent1 : palette.inputBg,
                      color: givingDateFrom === r.from && givingDateTo === r.to ? "#fff" : palette.text2,
                    }}
                  >
                    {r.label}
                  </button>
                ));
              })()}
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: palette.text2, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Minimum Amount</label>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <span style={{ position: "absolute", left: 10, fontSize: 13, color: palette.text3, fontWeight: 600 }}>$</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={givingMinAmount}
                    onChange={(e) => setGivingMinAmount(e.target.value)}
                    style={{ width: 120, padding: "8px 10px 8px 22px", borderRadius: 6, fontSize: 13, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: palette.text2, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>From</label>
                <input
                  type="date" value={givingDateFrom}
                  onChange={(e) => setGivingDateFrom(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: palette.text2, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>To</label>
                <input
                  type="date" value={givingDateTo}
                  onChange={(e) => setGivingDateTo(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: palette.text2, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Fund</label>
                <select
                  value={givingFundId}
                  onChange={(e) => setGivingFundId(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, fontFamily: FONT_FAMILY, outline: "none", minWidth: 140 }}
                >
                  <option value="">All Funds</option>
                  {(activeFunds || []).map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={runGivingQuery}
                disabled={givingLoading}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: givingLoading ? `${palette.accent1}70` : palette.accent1,
                  color: "#fff", fontSize: 13, fontWeight: 700,
                  cursor: givingLoading ? "wait" : "pointer", fontFamily: FONT_FAMILY,
                }}
              >
                <Filter size={ICON_SIZES.sm} />
                {givingLoading ? "Querying…" : "Apply"}
              </button>
              {givingMatchIds && (
                <button
                  onClick={clearGivingFilter}
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "8px 12px", borderRadius: 8,
                    border: "1px solid #fecaca", background: "#fef2f2",
                    color: "#dc2626", fontSize: 12, fontWeight: 600,
                    cursor: "pointer", fontFamily: FONT_FAMILY,
                  }}
                >
                  <X size={ICON_SIZES.sm} /> Clear
                </button>
              )}
            </div>
            {/* Contact Preference Exclusions */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ShieldAlert size={ICON_SIZES.sm} style={{ color: "#b45309" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: palette.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>Exclude:</span>
              </div>
              {[
                { key: "noEmail", label: "No Email", state: excludeNoEmail, setter: setExcludeNoEmail },
                { key: "noMail", label: "No Mail", state: excludeNoMail, setter: setExcludeNoMail },
                { key: "noPhone", label: "No Phone", state: excludeNoPhone, setter: setExcludeNoPhone },
                { key: "noText", label: "No Text", state: excludeNoText, setter: setExcludeNoText },
              ].map((pref) => (
                <label key={pref.key} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, color: pref.state ? "#b45309" : palette.text2, background: pref.state ? "#fef3c7" : "transparent", border: pref.state ? "1px solid #fde68a" : `1px solid ${palette.cardBorder}`, padding: "4px 10px", borderRadius: 6 }}>
                  <input type="checkbox" checked={pref.state} onChange={(e) => pref.setter(e.target.checked)} style={{ accentColor: "#d97706" }} />
                  {pref.label}
                </label>
              ))}
              <span style={{ width: 1, height: 20, background: palette.cardBorder, flexShrink: 0 }} />
              <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, color: requireEmail ? "#166534" : palette.text2, background: requireEmail ? "#dcfce7" : "transparent", border: requireEmail ? "1px solid #bbf7d0" : `1px solid ${palette.cardBorder}`, padding: "4px 10px", borderRadius: 6 }}>
                <input type="checkbox" checked={requireEmail} onChange={(e) => setRequireEmail(e.target.checked)} style={{ accentColor: "#16a34a" }} />
                Has Email
              </label>
            </div>
            {givingResults && givingMatchIds && (
              <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 8, background: `${palette.accent1}06`, border: `1px solid ${palette.accent1}20` }}>
                <div style={{ fontSize: 12, color: palette.text2, marginBottom: 10 }}>
                  <strong style={{ color: palette.text1 }}>{givingResults.matchCount}</strong> donor{givingResults.matchCount !== 1 ? "s" : ""} gave{" "}
                  <strong style={{ color: palette.text1 }}>{formatCurrency(parseFloat(givingMinAmount) || 0)}+</strong> between{" "}
                  {new Date(givingDateFrom + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} and{" "}
                  {new Date(givingDateTo + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}{" "}
                  <span style={{ color: palette.text3 }}>({givingResults.giftCount} total gifts from {givingResults.totalDonors} donors queried)</span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={handleCopyEmails} disabled={emailData.emails.length === 0} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: emailData.emails.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: emailData.emails.length > 0 ? `1px solid ${palette.accent1}40` : "1px solid #e5e7eb", background: emailData.emails.length > 0 ? `${palette.accent1}08` : "#f9fafb", color: emailData.emails.length > 0 ? palette.accent1 : "#9ca3af" }}><Copy size={ICON_SIZES.sm} /> Copy Emails ({emailData.emails.length})</button>
                  <button onClick={handleExport} disabled={filteredMembers.length === 0} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: filteredMembers.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: "none", background: filteredMembers.length > 0 ? "#10b981" : "#e5e7eb", color: filteredMembers.length > 0 ? "#fff" : "#9ca3af" }}><Download size={ICON_SIZES.sm} /> Export CSV ({filteredMembers.length})</button>
                  <button onClick={handleOpenInEmail} disabled={emailData.emails.length === 0} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: emailData.emails.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: emailData.emails.length > 0 ? "1px solid #6366f140" : "1px solid #e5e7eb", background: emailData.emails.length > 0 ? "#6366f108" : "#f9fafb", color: emailData.emails.length > 0 ? "#6366f1" : "#9ca3af" }}><ExternalLink size={ICON_SIZES.sm} /> Open in Email</button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Results Preview + Actions */}
      <Card style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: exclusionInfo.excludedCount > 0 ? 8 : 12, flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: palette.text1 }}>Preview ({filteredMembers.length} member{filteredMembers.length !== 1 ? "s" : ""})</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={openLoadPanelAndScroll} title="Load a previously saved list" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.text1 }}><FolderOpen size={ICON_SIZES.sm} /> Load{savedLists.length > 0 && <span style={{ fontSize: 11, color: palette.text3, fontWeight: 500 }}>({savedLists.length})</span>}</button>
            <button onClick={openSavePanelAndScroll} title="Save this list configuration for later reuse" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY, border: `1px solid ${palette.accent1}40`, background: `${palette.accent1}10`, color: palette.accent1 }}><Save size={ICON_SIZES.sm} /> {editingListId ? "Update List" : "Save List"}</button>
            <button onClick={handleCopyEmails} disabled={emailData.emails.length === 0} title="Copy email addresses to clipboard (semicolon-separated for Outlook)" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: emailData.emails.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: emailData.emails.length > 0 ? `1px solid ${palette.accent1}40` : "1px solid #e5e7eb", background: emailData.emails.length > 0 ? `${palette.accent1}08` : "#f9fafb", color: emailData.emails.length > 0 ? palette.accent1 : "#9ca3af" }}><Copy size={ICON_SIZES.sm} /> Copy Emails</button>
            <button onClick={handleOpenInEmail} disabled={emailData.emails.length === 0} title="Open your email client with all addresses in BCC" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: emailData.emails.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: emailData.emails.length > 0 ? "1px solid #6366f140" : "1px solid #e5e7eb", background: emailData.emails.length > 0 ? "#6366f108" : "#f9fafb", color: emailData.emails.length > 0 ? "#6366f1" : "#9ca3af" }}><ExternalLink size={ICON_SIZES.sm} /> Open in Email</button>
            <button onClick={handleExport} disabled={filteredMembers.length === 0} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: filteredMembers.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: "none", background: filteredMembers.length > 0 ? "#10b981" : "#e5e7eb", color: filteredMembers.length > 0 ? "#fff" : "#9ca3af" }}><Download size={ICON_SIZES.sm} /> Export CSV</button>
            <button onClick={() => setShowMarkSent(true)} disabled={filteredMembers.length === 0} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: filteredMembers.length === 0 ? "not-allowed" : "pointer", fontFamily: FONT_FAMILY, border: "none", background: filteredMembers.length > 0 ? palette.accent1 : "#e5e7eb", color: filteredMembers.length > 0 ? "#fff" : "#9ca3af" }}><CheckCircle size={ICON_SIZES.sm} /> Mark as Sent</button>
          </div>
        </div>

        {/* v1.3.0: Exclusion summary banner */}
        {exclusionInfo.excludedCount > 0 && filteredMembers.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 12, borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, fontWeight: 600, color: "#92400e" }}>
            <ShieldAlert size={ICON_SIZES.sm} style={{ flexShrink: 0 }} />
            {exclusionInfo.flag ? (
              <span>{exclusionInfo.excludedCount} member{exclusionInfo.excludedCount !== 1 ? "s" : ""} will be excluded from {exclusionInfo.label} output (opted out)</span>
            ) : (
              <span>{exclusionInfo.excludedCount} member{exclusionInfo.excludedCount !== 1 ? "s have" : " has"} contact preference opt-outs</span>
            )}
          </div>
        )}

        {filteredMembers.length === 0 ? (
          <div style={{ padding: "30px 20px", textAlign: "center", color: palette.text3 }}>
            <AlertCircle size={ICON_SIZES.xl} style={{ color: palette.text3, opacity: 0.3, marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>{filters.length === 0 ? "Add filters to build your list" : "No members match your current filters"}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", maxHeight: 400 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  {previewFields.map((f) => (<th key={f.key} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: palette.text2, borderBottom: `2px solid ${palette.cardBorder}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: palette.cardBg || "#fff" }}>{f.label}</th>))}
                  {givingResults && givingMatchIds && (
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: palette.accent1, borderBottom: `2px solid ${palette.cardBorder}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: palette.cardBg || "#fff" }}>Total Given</th>
                  )}
                  <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: palette.text2, borderBottom: `2px solid ${palette.cardBorder}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: palette.cardBg || "#fff", width: 40 }} title="Contact preference opt-outs"><ShieldAlert size={ICON_SIZES.sm} style={{ color: palette.text3, opacity: 0.5 }} /></th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.slice(0, 100).map((m) => {
                  const isExcluded = presetFlag ? m[presetFlag] === true : false;
                  const activeOuts = getActiveOptOuts(m);
                  const hasOuts = activeOuts.length > 0;
                  return (
                    <tr key={m.id} style={{ borderBottom: `1px solid ${palette.cardBorder}20`, opacity: isExcluded ? 0.5 : 1, background: isExcluded ? "#fffbeb08" : "transparent" }}>
                      {previewFields.map((f) => {
                        const raw = m[f.key];
                        const display = Array.isArray(raw) ? raw.map((v) => f.optionLabels?.[v] || v).join(", ") || "—" : String(raw ?? "");
                        const shouldStrike = isExcluded && ((presetFlag === "noEmail" && f.key === "email") || (presetFlag === "noMail" && ["homeStreet", "homeCity", "homeState", "homeZip"].includes(f.key)));
                        return (<td key={f.key} style={{ padding: "7px 10px", color: shouldStrike ? "#b45309" : palette.text1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200, textDecoration: shouldStrike ? "line-through" : "none" }}>{display}</td>);
                      })}
                      {givingResults && givingMatchIds && (
                        <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: palette.accent1, whiteSpace: "nowrap" }}>
                          {givingResults.memberTotals.has(String(m.id)) ? formatCurrency(givingResults.memberTotals.get(String(m.id))) : "—"}
                        </td>
                      )}
                      <td style={{ padding: "7px 6px", textAlign: "center", whiteSpace: "nowrap" }}>{hasOuts && <OptOutBadge labels={activeOuts} small />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredMembers.length > 100 && (<div style={{ padding: "8px 10px", fontSize: 11, color: palette.text3, textAlign: "center" }}>Showing first 100 of {filteredMembers.length} — all will be included in export</div>)}
          </div>
        )}
      </Card>

      {showMarkSent && (
        <MarkAsSentDialog filteredMembers={filteredMembers} palette={palette} onConfirm={handleMarkAsSent} onCancel={() => setShowMarkSent(false)} />
      )}
    </div>
  );
};